import {
  Component,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { IUserProfile } from '@store/common/user.interface';
import { MenuAction, MenuComponent, MenuDesktopConfirmComponent, MenuMobileComponent, MenuOption } from '../menu/menu.component';
import { TranslateModule } from '@ngx-translate/core';
import { IdFormatPipe } from '@core/pipes/id-format.pipe';
import { JsonStringifyPipe } from '@core/pipes/json-stringify.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { AddressSuggestionComponent } from '../address-suggestion/address-suggestion.component';
import { PbbSvgIconComponent } from '../pbb-svg-icon/pbb-svg-icon.component';
import { PbbRadioComponent } from '../pbb-radio';
import { PbbButtonComponent } from '../pbb-button/pbb-button.component';
import { AccountType } from '@api/index';
import { PbbNoticeComponent } from '../pbb-notice/pbb-notice.component';
import { NOTICE_TYPE } from '@core/constants';
import { PbbBankInfoComponent } from '../pbb-bank-info/pbb-bank-info.component';
import { StickyButtonDirective } from '@core/directives/btn-sticky.directive';
import { filter, Observable, take, takeUntil } from 'rxjs';
import { FinancialInstitution, LoaderState } from '@store/core/core.interface';
import { selectFinancialInstitutions } from '@store/core/core.selectors';
import { Store } from '@ngrx/store';
import { ILinkedFI } from '@store/user/user.interface';
import { isMobile } from '@core/utils';
import { MatDialog } from '@angular/material/dialog';
import { selectFI } from '@store/core/core.actions';
import { selectUserProfileRef } from '@store/user/user.selectors';
import { LetDirective } from '@ngrx/component';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { UserActions } from '@store/user/user.actions';

export const ACTION = {
  UNLINK: 'unlink',
  UNLINK_LAST: 'unlink_last',
  DEFAULT: 'default',
};

export interface SelectedAccount {
  selectedAccountId: string;
  selectedFiId: string;
  selectedFiUserId: string;
}

@Component({
  selector: 'pbb-payment-method-list',
  templateUrl: './payment-method-list.component.html',
  styleUrls: ['./payment-method-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentMethodListComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    AddressSuggestionComponent,
    MenuComponent,
    MatIconModule,
    MatListModule,
    MatChipsModule,
    MatRadioModule,
    ReactiveFormsModule,
    FormsModule,
    IdFormatPipe,
    JsonStringifyPipe,
    TranslateModule,
    PbbSvgIconComponent,
    PbbRadioComponent,
    PbbButtonComponent,
    PbbNoticeComponent,
    PbbBankInfoComponent,
    LetDirective,
    StickyButtonDirective,
  ],
})
export class PaymentMethodListComponent extends UntilDestroy implements ControlValueAccessor, OnChanges, OnInit {
  noticeType = NOTICE_TYPE;
  selectedAccount: string | undefined;
  @Input() defaultSelectedAccount!: string | null;
  @Input() userProfile!: IUserProfile | undefined;
  @Input() isCheckout = false;
  @Input() displayButtons = true;
  @Output() menuAction: EventEmitter<MenuAction> = new EventEmitter();
  @Output() continue: EventEmitter<void> = new EventEmitter();
  Action = ACTION;
  accountType = AccountType;
  financialInstitutions$: Observable<LoaderState<FinancialInstitution[]>> = this.store
    .select(selectFinancialInstitutions)
    .pipe(filter((finacialInstitutions) => !!finacialInstitutions));
  public readonly userProfileRef$ = this.store.select(selectUserProfileRef);

  fi: FinancialInstitution[] = [];

  showSave = false;
  showContinue = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    this.financialInstitutions$.pipe(take(1)).subscribe((fiState) => {
      this.fi = fiState.data || [];
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultSelectedAccount'] || changes['isCheckout']) {
      this.updateShowSave();
    }
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly store: Store,
    public dialog: MatDialog,
    private readonly _bottomSheet: MatBottomSheet
  ) {
    super();
  }

  saveSelection() {
    if (this.selectedAccount) {
      try {
        const account = JSON.parse(this.selectedAccount) as SelectedAccount;

        this.menuAction.emit({
          action: ACTION.DEFAULT,
          item: {
            linkedFiRef: account.selectedFiId,
            paymentMethodRef: account.selectedAccountId,
            fi_account_ref: account.selectedAccountId,
            fi_user_id: account.selectedFiUserId,
          },
        });

        this.defaultSelectedAccount = this.selectedAccount;
        this.updateShowSave();
      } catch (error) {
        console.error('Error parsing selected account:', error);
      }
    }
  }

  onContinue() {
    if (this.isCheckout) {
      this.saveSelection();
    }
    this.continue.emit();
  }

  private updateShowSave() {
    let show = false;
    let showContinueBtn = false;
    if (this.isCheckout) {
      showContinueBtn = this.selectedAccount !== this.defaultSelectedAccount;
    } else {
      show = this.selectedAccount !== this.defaultSelectedAccount;
    }
    if (this.showSave !== show || this.showContinue !== showContinueBtn) {
      this.showSave = show;
      this.showContinue = showContinueBtn;
      this.cdr.markForCheck();
    }
  }

  relinkFI(linkedFi: ILinkedFI, userProfileRef?: string) {
    const unlinkedFi = this.fi.find((fi) => fi.id === linkedFi.linked_fi_id);
    if (unlinkedFi) {
      this.fiSelected(unlinkedFi, userProfileRef);
    }
  }

  private fiSelected(selectedFI: FinancialInstitution, userProfileRef?: string, redirectUrl?: string) {
    const selectFIPayload: {
      selectedFI: FinancialInstitution;
      redirectUrl: string;
      userProfileRef?: string;
      rus?: boolean;
    } = {
      selectedFI,
      redirectUrl: redirectUrl || window.location.pathname,
      rus: false,
    };

    if (userProfileRef) {
      selectFIPayload.userProfileRef = userProfileRef;
    }
    this.store.dispatch(selectFI(selectFIPayload));
  }

  writeValue(value: any): void {
    this.selectedAccount = value;
    this.updateShowSave();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSelectionChange(value: string): void {
    this.selectedAccount = value;
    this.onChange(value);
    this.onTouched();
    this.updateShowSave();
  }

  unlinkFi(linkedFi: ILinkedFI) {
    const option: MenuOption = 
      {
        action: this.Action.UNLINK,
        label: 'COMMON.PAYMENT_METHOD.FI.MENU.UNLINK',
        icon: 'link_off',
        showConfirm: true,
        confirmTitle: ['COMMON.UNLINK_FI.UNLINK_BANK', { bankName: linkedFi.display_name }],
        confirmSubtitle: ['COMMON.UNLINK_FI.UNLINK_BANK_INFO', { bankName: linkedFi.display_name }],
        confirmButton: 'COMMON.UNLINK_FI.BUTTON.UNLINK',
        confirmColor: 'warn',
        logo: linkedFi.logo,
        showLogo: true,
        closeOnSuccess: true,
        confirmCancel: ['COMMON.BUTTON.CANCEL'],
        dialogSuccessMessageParams: { displayName: linkedFi.display_name },
      };
    if (isMobile()) {
      // Mobile flow for unlink FI
      const btmSheetRef = this.openMobileMenu(option, linkedFi);

      btmSheetRef.instance.onConfirm$.pipe(takeUntil(this.destroy$)).subscribe((option) => {
        this.store.dispatch(
          UserActions.unlinkFinancialInstituteStart({
            linkedFiRef: linkedFi.linked_fi_id || '',
            fiUserId: linkedFi.fi_user_id || '',
          })
        );
        if (!option.showConfirm) {
          btmSheetRef.dismiss();
        }
      });
    } else {
      // Desktop flow for unlink FI
      this.openDesktopMenu(option, linkedFi).componentInstance.onConfirm$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.store.dispatch(
          UserActions.unlinkFinancialInstituteStart({
            linkedFiRef: linkedFi.linked_fi_id || '',
            fiUserId: linkedFi.fi_user_id || '',
          })
        );
      });
    }
  }

  private openMobileMenu(option: MenuOption, linkedFi: ILinkedFI) {
    return this._bottomSheet.open(MenuMobileComponent, {
        data: {
          options: [option],
          item: linkedFi,
          itemRef: linkedFi.linked_fi_id,
          index: 0,
          currentDisplay: option.action,
        },
        panelClass: 'content_main-drawer',
        autoFocus: false,
        backdropClass: 'content_main-bottom-sheet',
      })
  }

  private openDesktopMenu(option: MenuOption, linkedFi: ILinkedFI) {
    return this.dialog.open(MenuDesktopConfirmComponent, {
        data: {
          option: option,
          item: linkedFi,
          itemRef: linkedFi.linked_fi_id,
          index: 0,
        },
        closeOnNavigation: true,
        autoFocus: false,
        width: '416px',
        panelClass: 'content_overlay-medium',
        backdropClass: 'content_overlay-dialog',
      });
  }
}
